import { useId, useState } from 'react'
import { useBahasa } from './i18n'
import { useSesi } from './auth'
import PemilihBahasa from './PemilihBahasa'
import { DI_DEV, HOST, LINGKUNGAN } from '../shared/lib/lingkungan'
import { Bendera } from '../shared/ui'

/**
 * Satu-satunya layar yang BELUM TAHU warna aksennya.
 *
 * Ketiga antarmuka masuk lewat pintu yang sama — `POST /dashboard/login` — dan
 * peran baru diketahui SESUDAH token diterima. Jadi layar ini tidak boleh memakai
 * aksen shell mana pun; ia memakai netral, dan identitas produk dibawa oleh
 * markahnya, bukan oleh warna.
 *
 * NETRAL SAMPAI KE CINCIN FOKUS. `:focus-visible` global memakai `var(--accent)`, dan
 * tanpa shell yang menimpanya nilainya jatuh ke bawaan `:root` — plum milik Tenant
 * Marketing. Karena itu `--accent` ditimpa ke `--ink` di akar layar ini, bukan per
 * elemen: isian, tombol, dan pemilih bahasa ikut sekaligus, di kedua tema.
 *
 * Lingkungan disebut terang-terangan di bawah. Dev dan produksi punya dua alamat
 * yang mirip, dan satu-satunya cara membedakannya sebelum masuk adalah membacanya
 * di sini. Itu aturan yang sama dengan bar konteks tenant: **yang menentukan
 * akibat tidak boleh ditebak.**
 *
 * ISIAN 16px DI PERANGKAT SENTUH. iOS Safari memperbesar halaman saat kontrol di bawah
 * 16px difokuskan, dan pembesarannya terbawa ke dashboard sesudah masuk. Ditentukan
 * oleh jenis penunjuk, bukan lebar layar — HP yang dimiringkan lebih lebar dari `sm`.
 *
 * KARTU DITAMBATKAN KE ATAS, tidak dipusatkan. Yang dipusatkan melompat naik setengah
 * tinggi galat begitu galat muncul, dan tombol yang baru diklik bergeser dari bawah kursor.
 */

function Markah() {
  return (
    <span
      className="grid place-items-center w-9 h-9 rounded-[10px] bg-ink text-ground shrink-0"
      aria-hidden="true"
    >
      <svg width="19" height="19" viewBox="0 0 20 20" fill="none">
        {/* Pesan masuk, lalu jawaban keluar — bentuk produk ini dalam satu markah. */}
        <path
          d="M3 6.5A2.5 2.5 0 0 1 5.5 4h6A2.5 2.5 0 0 1 14 6.5v2A2.5 2.5 0 0 1 11.5 11H7l-4 3V6.5Z"
          fill="currentColor"
        />
        <circle cx="15" cy="14.5" r="2.5" fill="currentColor" opacity=".55" />
      </svg>
    </span>
  )
}

function Ikon({ children, ukuran = 18 }) {
  return (
    <svg
      width={ukuran}
      height={ukuran}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  )
}

/**
 * Panel kiri, hanya di layar lebar: apa yang dikerjakan produk ini.
 *
 * Isinya DESKRIPSI, bukan janji. Tidak ada klaim keamanan atau angka — layar yang
 * dibuka sebelum orang masuk tidak boleh menyatakan sesuatu yang belum bisa kita
 * buktikan, dan klaim yang kelak terbukti keliru merusak kepercayaan yang justru
 * ingin dibangun panel ini.
 *
 * `pulau-gelap` memasang palet gelap di dalam panel saja (index.css), jadi warnanya
 * tetap dari token. Di tema gelap warnanya sama dengan halaman, jadi garis kanannya
 * yang memisahkan keduanya.
 */
function PanelProduk() {
  const { t } = useBahasa()
  const fitur = [
    {
      teks: t('login.fitur1'),
      ikon: <path d="M5 5h14a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-8l-5 4v-4H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z" />,
    },
    {
      teks: t('login.fitur2'),
      ikon: (
        <>
          <circle cx="12" cy="8" r="3.5" />
          <path d="M5 20a7 7 0 0 1 14 0" />
        </>
      ),
    },
    {
      teks: t('login.fitur3'),
      ikon: (
        <>
          <rect x="4" y="5" width="16" height="15" rx="2" />
          <path d="M4 10h16M9 3v4M15 3v4" />
        </>
      ),
    },
  ]

  return (
    <aside className="pulau-gelap relative hidden lg:flex flex-col justify-between gap-10 overflow-hidden bg-ground text-ink border-r border-line px-12 py-11 xl:px-16">
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-kisi-halus" />

      <div className="relative flex items-center gap-3">
        <Markah />
        <b className="text-[17px] font-bold tracking-[-.02em]">{t('app.nama')}</b>
      </div>

      <div className="relative max-w-[440px]">
        <p className="m-0 text-[32px] leading-[1.15] font-semibold tracking-[-.025em]">
          {t('login.panelJudul')}
        </p>
        <ul className="mt-9 mb-0 p-0 list-none flex flex-col gap-5">
          {fitur.map(({ teks, ikon }) => (
            <li key={teks} className="flex items-start gap-3.5">
              <span className="grid place-items-center w-9 h-9 rounded-[8px] border border-line bg-surface text-ink2 shrink-0">
                <Ikon>{ikon}</Ikon>
              </span>
              <span className="pt-[7px] text-[14px] leading-[1.5] text-ink2">{teks}</span>
            </li>
          ))}
        </ul>
      </div>

      <p className="relative m-0 text-[13px] text-ink3 max-w-[44ch] text-balance">{t('login.satuPintu')}</p>
    </aside>
  )
}

const ID_GALAT = 'login-galat'

function Kolom({ label, galat, akhiran, ...sisa }) {
  const id = useId()
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[13px] font-medium text-ink2">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          {...sisa}
          aria-invalid={galat ? true : undefined}
          aria-describedby={galat ? ID_GALAT : undefined}
          className={`w-full h-11 text-base [@media(pointer:fine)]:text-[14px] text-ink bg-surface border border-ink3
                      rounded-[8px] px-3.5 transition-colors hover:border-ink2 ${akhiran ? 'pr-12' : ''}`}
        />
        {akhiran}
      </div>
    </div>
  )
}

function Putar() {
  return (
    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity=".3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  )
}

export default function Login() {
  const { t } = useBahasa()
  const { masuk, kedaluwarsa } = useSesi()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [sedang, setSedang] = useState(false)
  const [galat, setGalat] = useState(null)
  // Tampilan saja — tidak ikut terkirim, tidak menyentuh `kirim()`.
  const [lihatSandi, setLihatSandi] = useState(false)

  async function kirim(e) {
    e.preventDefault()
    setGalat(null)
    setSedang(true)
    try {
      await masuk(username, password)
    } catch (err) {
      // Pesan datang dari backend apa adanya ("Email atau kata sandi salah.") dan
      // tidak diterjemahkan — lihat catatan di i18n.jsx.
      setGalat(err?.kode === 'jaringan' ? t('umum.jaringanMati') : err.message)
    } finally {
      setSedang(false)
    }
  }

  return (
    <div className="relative min-h-full grid lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] bg-ground [--accent:var(--ink)]">
      <PanelProduk />

      <main className="relative flex flex-col overflow-hidden px-5 pt-5 pb-8 sm:px-10 lg:px-16">
        {/* Latar: dua lapis sangat tipis, bukan gradien mencolok. Layar ini dibuka
            tiap pagi; yang mencolok jadi melelahkan pada kali kelima puluh. Kisinya
            hanya di layar sempit — di layar lebar panel kiri sudah membawanya. */}
        <div aria-hidden className="pointer-events-none absolute inset-0 bg-latar-halus" />
        <div aria-hidden className="pointer-events-none absolute inset-0 bg-kisi-halus lg:hidden" />

        <header className="relative flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 lg:hidden">
            <Markah />
            <div className="leading-tight">
              <div className="text-[18px] font-bold tracking-[-.02em]">{t('app.nama')}</div>
              <div className="text-[12px] text-ink2">{t('app.deskripsi')}</div>
            </div>
          </div>
          {/* Pemilih bahasa dipakai juga di rel navigasi, jadi ukurannya disesuaikan
              dari sini, bukan di komponennya. */}
          <div
            className="ml-auto shrink-0 [&_select]:h-9 [&_select]:px-2.5 [&_select]:rounded-[8px]
                       [&_select]:border-ink3 [&_select]:text-base [@media(pointer:fine)]:[&_select]:text-[13px]"
          >
            <PemilihBahasa />
          </div>
        </header>

        <div className="relative flex-1 grid justify-items-center content-start pt-6 pb-8 sm:pt-[10vh] lg:pt-[15vh]">
          <div className="w-full max-w-[400px] flex flex-col gap-6">
            <div className="bg-surface border border-line rounded-[12px] shadow-kartu">
              <div className="px-6 pt-7 sm:px-8">
                <h1 className="m-0 text-[22px] font-semibold tracking-[-.02em]">
                  {t('login.selamat')}
                </h1>
                <p className="mt-1.5 mb-0 text-[14px] text-ink2">{t('login.keterangan')}</p>
              </div>

              <form onSubmit={kirim} className="px-6 pt-6 pb-7 sm:px-8 flex flex-col gap-4">
                {/* Huruf besar otomatis DIMATIKAN: keyboard HP menulis `Admin@…`. Email
                    dicocokkan tanpa membedakan huruf besar-kecil (ADR-120), tetapi nama
                    pengguna lama dicocokkan persis. */}
                <Kolom
                  label={t('login.username')}
                  name="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  inputMode="email"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  galat={galat}
                  autoFocus
                  required
                />
                <Kolom
                  label={t('login.password')}
                  name="password"
                  type={lihatSandi ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  galat={galat}
                  required
                  akhiran={
                    <button
                      type="button"
                      onClick={() => setLihatSandi((v) => !v)}
                      aria-label={t('login.tampilkanSandi')}
                      aria-pressed={lihatSandi}
                      title={t('login.tampilkanSandi')}
                      className="absolute inset-y-0 right-0 w-11 grid place-items-center
                                 text-ink3 hover:text-ink bg-transparent border-0 rounded-r-[8px] cursor-pointer"
                    >
                      <Ikon>
                        <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
                        <circle cx="12" cy="12" r="3" />
                        {lihatSandi && <path d="M4 4l16 16" />}
                      </Ikon>
                    </button>
                  }
                />

                {/* Sesi habis BUKAN galat — orangnya tidak melakukan kesalahan apa pun.
                    Warnanya kuning, bukan merah, dan ia hilang begitu ada galat sungguhan. */}
                {kedaluwarsa && !galat && (
                  <Bendera nada="warn" role="status">{t('umum.sesiHabis')}</Bendera>
                )}
                {galat && (
                  <Bendera id={ID_GALAT} nada="bad" role="alert">{galat}</Bendera>
                )}

                <button
                  type="submit"
                  disabled={sedang}
                  className="mt-1 w-full h-11 inline-flex items-center justify-center gap-2
                             rounded-[8px] px-4 text-[14px] font-semibold
                             bg-ink text-ground border border-ink cursor-pointer
                             transition-opacity hover:opacity-90
                             disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {sedang && <Putar />}
                  {sedang ? t('login.sedang') : t('login.tombol')}
                </button>
              </form>

              <p className="m-0 px-6 py-4 sm:px-8 border-t border-line text-[13px] text-ink2 text-center text-balance">
                {t('login.lupa')}
              </p>
            </div>

            <footer className="flex flex-col gap-2.5 items-center text-center">
              <p className="m-0 text-[13px] text-ink2 max-w-[36ch] text-balance lg:hidden">{t('login.satuPintu')}</p>
              <span className="inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[12px] text-ink2">
                <i aria-hidden className={`w-1.5 h-1.5 rounded-full ${DI_DEV ? 'bg-warn' : 'bg-ok'}`} />
                {t('login.lingkungan')}
                <b className="font-mono font-medium text-ink">{HOST}</b>
                {DI_DEV && (
                  <span className="text-[10.5px] font-semibold uppercase tracking-[.06em] text-warn border border-lineStrong rounded px-1 py-px">
                    {LINGKUNGAN}
                  </span>
                )}
              </span>
            </footer>
          </div>
        </div>
      </main>
    </div>
  )
}
